import React, { useEffect, useMemo, useState } from "react";

import {
  Checkbox,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Input,
  Radio,
  SemanticWIDTHS,
} from "semantic-ui-react";

import ModSearchLocationMap, { ConfirmLocationHandler } from "./ModSearchLocationMap";

type OfficialAccountAddressFormProps = {
  data: OfficialAccountAddressDataType;
  error?: Record<string, any>;
  readOnly?: boolean;
  width?: SemanticWIDTHS;
  masterData?: Partial<Record<string, any[]>>;
  masterOptions?: Partial<Record<string, any[]>>;
  onChangeValue?: (e: any, data: any) => void;
};

export type OfficialAccountAddressDataType = Partial<{
  oa_type: "ORGANIZATION" | "PUBLIC";
  division_address: {
    city: string;
    district: string;
    full_address_text: string;
    province: string;
    zipcode: string;
    place_id?: string;
    latitude?: string;
    longitude?: string;
  };
  has_staff: boolean;
  business?: {
    business_type?: string;
    branch_type?: string;
    branch_name?: number;
    first_name?: string;
    last_name?: string;
    business_name?: string;
    tax_payer_id?: string;
  };
}>;

const OfficialAccountAddressForm = (props: OfficialAccountAddressFormProps) => {
  const [openModMapLocation, setOpenModMapLocation] = useState<boolean>(false);
  const [isCurrentLocation, setIsCurrentLocation] = useState<boolean>(false);

  useEffect(() => {
    if (props.data.oa_type === undefined) {
      props.onChangeValue?.(null, { name: "oa_type", value: "PUBLIC" });
    }

    if (props.data.has_staff === undefined) {
      props.onChangeValue?.(null, { name: "has_staff", value: true });
    }
  }, [props.data]);

  const handleChangeProvince = (e: any, data: any) => {
    props.onChangeValue?.(null, { name: "division_address.province", value: data.value });
    props.onChangeValue?.(null, { name: "division_address.district", value: "" });
    props.onChangeValue?.(null, { name: "division_address.city", value: "" });
    props.onChangeValue?.(null, { name: "division_address.zipcode", value: "" });

    handleResetLocation();
  };

  const handleChangeDistrict = (e: any, data: any) => {
    props.onChangeValue?.(null, { name: "division_address.district", value: data.value });
    props.onChangeValue?.(null, { name: "division_address.city", value: "" });
    props.onChangeValue?.(null, { name: "division_address.zipcode", value: "" });

    handleResetLocation();
  };

  const handleChangeCity = (e: any, data: any) => {
    const foundCity = (props.masterData?.city ?? []).find((item) => item.id === data.value);

    props.onChangeValue?.(null, { name: "division_address.city", value: data.value });
    props.onChangeValue?.(null, {
      name: "division_address.zipcode",
      value: foundCity?.zipcode || "",
    });

    handleResetLocation();
  };

  const handleResetLocation = () => {
    props.onChangeValue?.(null, { name: "division_address.latitude", value: "" });
    props.onChangeValue?.(null, { name: "division_address.longitude", value: "" });
    props.onChangeValue?.(null, { name: "division_address.place_id", value: "" });
  };

  const handleOpenModMapLocation = () => {
    if (!props.readOnly) {
      setOpenModMapLocation(true);
    }
  };

  const handleCloseModMapLocation = () => {
    setOpenModMapLocation(false);
    setIsCurrentLocation(false);
  };

  const handleConfirmLocation: ConfirmLocationHandler = (locationData) => {
    props.onChangeValue?.(null, {
      name: "division_address.province",
      value: locationData.province,
    });
    props.onChangeValue?.(null, {
      name: "division_address.district",
      value: locationData.district,
    });
    props.onChangeValue?.(null, { name: "division_address.city", value: locationData.city });
    props.onChangeValue?.(null, { name: "division_address.zipcode", value: locationData.zipcode });
    props.onChangeValue?.(null, {
      name: "division_address.full_address_text",
      value: locationData.full_address_text,
    });
    props.onChangeValue?.(null, {
      name: "division_address.latitude",
      value: locationData.latitude,
    });
    props.onChangeValue?.(null, {
      name: "division_address.longitude",
      value: locationData.longitude,
    });
    props.onChangeValue?.(null, {
      name: "division_address.place_id",
      value: locationData.place_id,
    });

    handleCloseModMapLocation();
  };

  const handleChangePrivate = (e: any, data: any) => {
    props.onChangeValue?.(null, data);
    props.onChangeValue?.(null, { name: "has_staff", value: true });
  };

  const districtOptions = useMemo(() => {
    if (!props.data.division_address?.province) return [];

    return (props.masterData?.district ?? [])
      .filter((d: any) => d.area === props.data?.division_address?.province)
      .map((d: any) => ({
        key: d.id,
        text: d.name,
        value: d.id,
      }));
  }, [props.masterData?.district, props.data.division_address?.province]);

  const cityOptions = useMemo(() => {
    if (!props.data.division_address?.district) return [];

    return (props.masterData?.city ?? [])
      .filter((c: any) => c.area === props.data?.division_address?.district)
      .map((c: any) => ({
        key: c.id,
        text: c.name,
        value: c.id,
      }));
  }, [props.masterData?.city, props.data.division_address?.district]);

  return (
    <>
      <FormGroup inline>
        {!!props.width && (
          <FormField width={props.width}>
            <label>ประเภทของ Official</label>
          </FormField>
        )}
        <FormField style={{ alignItems: "baseline" }} width={12} inline>
          {!props.width && <label>ประเภทของ Official</label>}
          <div>
            <Radio
              style={{ marginRight: "2rem" }}
              checked={props.data.oa_type === "ORGANIZATION"}
              disabled={props.readOnly}
              label="ภายในองค์กร"
              name="oa_type"
              value="ORGANIZATION"
              onChange={handleChangePrivate}
            />
            <Radio
              style={{ marginRight: "2rem" }}
              checked={props.data.oa_type === "PUBLIC"}
              disabled={props.readOnly}
              label="สาธารณะ"
              name="oa_type"
              value="PUBLIC"
              onChange={props.onChangeValue}
            />
            {props.data.oa_type === "PUBLIC" && (
              <>
                <Checkbox
                  style={{ marginRight: "2rem" }}
                  checked={props.data.has_staff === true}
                  disabled={props.readOnly}
                  label="มีเจ้าหน้าที่ดูแล"
                  name="has_staff"
                  onChange={(e, data) => {
                    props.onChangeValue?.(e, { ...data, checked: true });
                  }}
                />
                <Checkbox
                  checked={props.data.has_staff === false}
                  disabled={props.readOnly}
                  label="ไม่มีเจ้าหน้าที่ดูแล"
                  name="has_staff"
                  onChange={(e, data) => {
                    props.onChangeValue?.(e, { ...data, checked: false });
                  }}
                />
              </>
            )}
          </div>
        </FormField>
      </FormGroup>
      <FormGroup style={{ alignItems: "flex-start" }} inline>
        {!!props.width && (
          <FormField width={props.width} required>
            <label style={{ marginTop: "1.8rem" }}>สถานที่ตั้ง</label>
          </FormField>
        )}
        <FormField
          style={{ alignItems: "flex-start" }}
          width={(16 - Number(props.width || "")) as SemanticWIDTHS}
          inline
          required
        >
          {!props.width && <label style={{ marginTop: "2rem" }}>สถานที่ตั้ง</label>}
          <Form style={{ paddingLeft: "0.5rem", width: "100%" }}>
            <FormGroup style={{ alignItems: "flex-start" }}>
              <FormField
                style={{ display: "grid" }}
                width={10}
                required
                error={props.error?.full_address_text ?? false}
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>ที่อยู่</label>
                <Input
                  name="division_address.full_address_text"
                  readOnly={props.readOnly}
                  value={props.data.division_address?.full_address_text || ""}
                  fluid
                  onChange={props.onChangeValue}
                />
              </FormField>
              <FormField>
                <img
                  style={{ cursor: props.readOnly ? "" : "pointer", width: "145px" }}
                  alt="find-location-map.icon"
                  src="/static/images/icon-find-location-map.png"
                  onClick={handleOpenModMapLocation}
                />
              </FormField>
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <FormField
                style={{ display: "grid" }}
                width={4}
                required
                error={props.error?.province ?? false}
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>จังหวัด</label>
                <Dropdown
                  className="inline-label"
                  style={{ width: "100%" }}
                  disabled={props.readOnly}
                  placeholder="-- กรุณาเลือกจังหวัด --"
                  value={props.data.division_address?.province || ""}
                  fluid
                  search
                  selection
                  options={props.masterOptions?.province}
                  onChange={handleChangeProvince}
                />
              </FormField>
              <FormField
                style={{ display: "grid" }}
                width={3}
                required
                error={props.error?.district ?? false}
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>อำเภอ /แขวง</label>
                <Dropdown
                  className="inline-label"
                  style={{ width: "100%" }}
                  disabled={!props.data.division_address?.province || props.readOnly}
                  placeholder="-- กรุณาเลือกอำเภอ /แขวง --"
                  value={props.data.division_address?.district || ""}
                  fluid
                  search
                  selection
                  options={districtOptions}
                  onChange={handleChangeDistrict}
                />
              </FormField>
              <FormField
                style={{ display: "grid" }}
                width={3}
                required
                error={props.error?.city ?? false}
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>ตำบล /เขต</label>
                <Dropdown
                  className="inline-label"
                  style={{ width: "100%" }}
                  placeholder="-- กรุณาเลือกตำบล /เขต --"
                  value={props.data.division_address?.city || ""}
                  fluid
                  search
                  selection
                  options={cityOptions}
                  onChange={handleChangeCity}
                  disabled={
                    !props.data.division_address?.province ||
                    !props.data.division_address.district ||
                    props.readOnly
                  }
                />
              </FormField>
              <FormField style={{ display: "grid" }} width={3}>
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>รหัสไปรษณีย์</label>
                <Input value={props.data.division_address?.zipcode || ""} fluid readOnly />
              </FormField>
            </FormGroup>
          </Form>
        </FormField>
      </FormGroup>

      <ModSearchLocationMap
        isCurrentLocation={isCurrentLocation}
        open={openModMapLocation}
        masterData={props.masterData}
        onClose={handleCloseModMapLocation}
        onConfirm={handleConfirmLocation}
      />
    </>
  );
};

OfficialAccountAddressForm.displayName = "OfficialAccountAddressForm";

export default React.memo(OfficialAccountAddressForm);
